/**
 * Priority-driven semantic colors for status badges/chips/dots.
 *
 * RULE: saturated color communicates **clinical priority only**. Data, workflow,
 * and "no action needed" states are neutral gray so the loudest thing on a dense
 * clinical screen is always a real medical alert — never a data gap or a button.
 *
 * Tone hierarchy (loud → quiet):
 *   critical  — immediate clinical action (e.g. hypoglycemia)        ← loudest
 *   alert     — abnormal value needing attention (e.g. hyperglycemia)
 *   attention — worth a look, not an alarm (e.g. needs review)        ← quiet amber
 *   positive  — healthy / stable                                      ← quiet green
 *   neutral   — data gap / pending / inactive / informational         ← gray
 *
 * Use STATUS_TONES for the chip colors and severityTone() to map a
 * domain value to a tone. See docs/UI-STYLE-GUIDE.md and CLAUDE.md (UI Guidelines).
 */

export type StatusTone = 'critical' | 'alert' | 'attention' | 'positive' | 'neutral'

export interface StatusStyle {
  bgcolor: string
  color: string
  borderColor: string
}

export const STATUS_TONES: Record<StatusTone, StatusStyle> = {
  critical: { bgcolor: '#fee2e2', color: '#b91c1c', borderColor: '#fca5a5' },
  alert: { bgcolor: '#ffedd5', color: '#c2410c', borderColor: '#fdba74' },
  attention: { bgcolor: '#fef6e7', color: '#b45309', borderColor: '#fde3b3' },
  positive: { bgcolor: '#f0fdf4', color: '#15803d', borderColor: '#dcfce7' },
  neutral: { bgcolor: '#f3f4f6', color: '#4b5563', borderColor: '#e5e7eb' },
}

/** Solid accent per tone — for dots, indicators, and small markers. */
export const TONE_ACCENT: Record<StatusTone, string> = {
  critical: '#dc2626',
  alert: '#ea580c',
  attention: '#d97706',
  positive: '#16a34a',
  neutral: '#9ca3af',
}

/** Map a generic severity to a tone (alerts are the only loud colors). */
export const severityTone = (
  severity: 'critical' | 'high' | 'medium' | 'low' | string,
): StatusTone => {
  switch (severity) {
    case 'critical':
      return 'critical'
    case 'high':
      return 'alert'
    case 'medium':
      return 'attention'
    default:
      return 'neutral'
  }
}
